import { Button, DynamicTextEditor, HtmlSanitizer, Input, ISelectOption, Select } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { cloneDeep } from 'lodash';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import {
	DistributionType,
	EmailTemplatePropertiesUnderValidation,
	IAudience,
	IEmail,
	IEmailTemplate,
	IValidationErrorCollection,
} from '../../../models';
import { DistributionHelper } from '../../../utilities/DistributionHelper';
import { ValidationErrorHelper } from '../../../utilities/ValidationErrorHelper';
import { Col3Col9, EnableHtml, WarningModal } from '../../common';
import { ButtonErrorCss, ErrorMessageLabel } from '../SharedComponents';
import { SurveyLink } from '../SurveyLink';
import { ToastType } from '../ToastType';
import { ReplacementTags } from './ReplacementTags';
import { SendEmailModal } from './SendEmailModal';

export const InviteEmailContainer = styled.div`
	${(props: { isAdminMenu?: boolean }) => !props.isAdminMenu && 'padding-top: 40px;'}
	padding-bottom: 20px;
	textarea {
		min-height: 250px;
	}
`;

export const SendTestEmailButton = styled(Button)<{ error?: string }>`
	&& {
		${(props) => (props.error === 'true' ? ButtonErrorCss : '')}
	}
`;
SendTestEmailButton.displayName = 'SendTestEmailButton';
const ErrorIcon = styled.img`
	margin-left: 10px;
	height: 20px;
	width: 20px;
`;
ErrorIcon.displayName = 'ErrorIcon';

export const NoteContainer = styled.div`
	height: 39px;
	width: 623px;
	color: #444444;
	font-family: Montserrat;
	font-size: 15px;
	font-weight: 500;
	line-height: 19px;
`;

const TemplatesSelectContainer = styled.div`
	background-color: #eeeeee;
	padding: 20px;
	margin-bottom: 20px;
`;

export interface IInviteEmailProps {
	audience: IAudience;
	defaultEmailTemplates: IEmailTemplate[];
	invitationEmail: IEmailTemplate;
	inviteEmailValidationError?: IValidationErrorCollection;
	isAdminMenu?: boolean;
	isTemplateWarningModalOpen: boolean;
	readOnly?: boolean;
	sendEmailModalOpen: boolean;
	isMultiLanguageSurvey?: boolean;
	selectedDefaultTemplate?: IEmailTemplate;
	shouldDisableTestEmail: boolean;
	surveyId?: number;
	CloseTemplateWarningModal: () => any;
	onChange: (emailTeplate: IEmailTemplate) => any;
	toast: (type: ToastType, message: string) => any;
	selectDefaultTemplate: (values?: any) => any;
	sendEmail?: (email: IEmail) => any;
	submitTemplateWarningModal: () => any;
	toggleSendEmailModal: (open: boolean) => any;
	validateInvitationEmail: (distributionType?: DistributionType, fieldNames?: EmailTemplatePropertiesUnderValidation[]) => any;
}

export class InviteEmail extends React.Component<
	IInviteEmailProps,
	{
		emailAddresses: string;
		validationError?: IValidationErrorCollection;
	}
> {
	constructor(props: IInviteEmailProps) {
		super(props);

		this.state = {
			emailAddresses: '',
		};

		this.closeModal = this.closeModal.bind(this);
		this.onChange = this.onChange.bind(this);
		this.addPlaceholder = this.addPlaceholder.bind(this);
		this.onEmailContentChange = this.onEmailContentChange.bind(this);
		this.onEnableHtmlContentValueChange = this.onEnableHtmlContentValueChange.bind(this);
	}

	public componentDidUpdate() {
		if (this.props.invitationEmail.audienceId === 0 && this.props.audience.audienceId !== 0) {
			this.props.onChange({ ...this.props.invitationEmail, audienceId: this.props.audience.audienceId });
		}
	}

	public render() {
		const {
			audience,
			defaultEmailTemplates,
			invitationEmail,
			inviteEmailValidationError,
			isAdminMenu,
			readOnly,
			selectedDefaultTemplate,
			sendEmailModalOpen,
			isMultiLanguageSurvey,
		} = this.props;
		const { validationError } = this.state;
		const validationErrorHelper = new ValidationErrorHelper<EmailTemplatePropertiesUnderValidation>(inviteEmailValidationError);
		const stateValidationErrorHelper = new ValidationErrorHelper<EmailTemplatePropertiesUnderValidation>(validationError);
		const templateSelectItems: ISelectOption[] = defaultEmailTemplates.map((t, index) => {
			return {
				name: t.emailTemplateId + '',
				displayText: t.emailTemplateName,
				displayOrder: index,
			};
		});
		const selectedTemplate: ISelectOption | undefined = templateSelectItems.find(
			(t) => t.name === selectedDefaultTemplate?.emailTemplateId + '',
		);

		return (
			<>
				<InviteEmailContainer className='tws-invite-email' isAdminMenu={isAdminMenu}>
					<Col3Col9 label='Invite Email' labelSize='lg' />
					{readOnly && (
						<NoteContainer>
							<b>{ContentConstants.SURVEY_ADMIN_INVITE_EMAIL_NOTE}</b>
							{ContentConstants.SURVEY_ADMIN_INVITE_EMAIL_NOTE_CONTENT}
						</NoteContainer>
					)}
					{!readOnly && (
						<TemplatesSelectContainer>
							<Col3Col9 label={ContentConstants.INVITE_EMAIL_TEMPLATES}>
								<p>{ContentConstants.INVITE_EMAIL_TEMPLATES_INSTRUCTIONS}</p>
								<Select
									items={templateSelectItems}
									size='md'
									selectWidth='45%'
									onApply={this.props.selectDefaultTemplate}
									selectedValue={selectedTemplate?.name}
									aria-label={ContentConstants.INVITE_EMAIL_TEMPLATES}
								/>
							</Col3Col9>
						</TemplatesSelectContainer>
					)}
					<Col3Col9
						label={ContentConstants.INVITE_EMAIL_FROM}
						labelInfo={ContentConstants.INSTRUCTIONALTEXT_INVITE_EMAIL_FROM}
						labelInfoTitle={ContentConstants.INVITE_EMAIL_FROM}
					>
						<Input
							id='invitationEmailFrom'
							value={invitationEmail.from}
							maxLength={50}
							error={!validationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.From)}
							message={validationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.From)}
							onChange={(event: any) => this.onChange('from', event)}
							placeholder={ContentConstants.INVITE_EMAIL_PLACEHOLDER_FROM}
							disabled={readOnly}
							aria-label={ContentConstants.INVITE_EMAIL_FROM}
						/>
					</Col3Col9>
					<Col3Col9
						label={ContentConstants.INVITE_EMAIL_REPLYTO}
						labelInfo={ContentConstants.INSTRUCTIONALTEXT_INVITE_EMAIL_REPLY}
						labelInfoTitle={ContentConstants.INVITE_EMAIL_REPLYTO}
					>
						<Input
							id='invitationEmailReplyToEmail'
							value={invitationEmail.replyToEmail}
							maxLength={100}
							error={!validationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.ReplyToEmail)}
							message={validationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.ReplyToEmail)}
							onChange={(event: any) => this.onChange('replyToEmail', event)}
							placeholder={ContentConstants.INVITE_EMAIL_PLACEHOLDER_REPLYTO}
							disabled={readOnly}
							aria-label={ContentConstants.INVITE_EMAIL_REPLYTO}
						/>
					</Col3Col9>
					<Col3Col9 label={'Subject Line'}>
						<Input
							id='invitationEmailSubject'
							value={invitationEmail.subject}
							maxLength={150}
							error={!validationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.Subject)}
							message={validationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.Subject)}
							onChange={(event: any) => this.onChange('subject', event)}
							placeholder={ContentConstants.INPUT_PLACEHOLDER_INVITE_EMAIL_SUBJECT}
							disabled={readOnly}
							aria-label={'Subject Line'}
						/>
					</Col3Col9>
					<Col3Col9 label={'Content'}>
						{!readOnly && (
							<>
								<p>{ContentConstants.INSTRUCTIONALTEXT_INVITE_EMAIL_CONTENT}</p>
								{audience.distributionType === DistributionType.SSO && <p>{ContentConstants.INSTRUCTIONALTEXT_INVITE_EMAIL_CONTENT_SSO}</p>}
								<EnableHtml enableHtmlContent={invitationEmail.enableHtmlContent} getValue={this.onEnableHtmlContentValueChange} />
							</>
						)}
						<div style={{ display: 'flex' }}>
							<div style={{ width: '100%' }}>
								<DynamicTextEditor
									id='invitationEmailContent'
									inputType='textarea'
									height='210px'
									placeholder={ContentConstants.INPUT_PLACEHOLDER_INVITE_EMAIL_CONTENT}
									enableHtmlEditor={invitationEmail.enableHtmlContent}
									error={
										!validationErrorHelper.IsAllValid([
											EmailTemplatePropertiesUnderValidation.Content,
											EmailTemplatePropertiesUnderValidation.SurveyLink,
										])
									}
									messages={validationErrorHelper.AllErrors([
										EmailTemplatePropertiesUnderValidation.Content,
										EmailTemplatePropertiesUnderValidation.SurveyLink,
									])}
									readOnly={readOnly}
									value={invitationEmail.content}
									onChange={this.onEmailContentChange}
									htmlEditorMode={'custom'}
									toolbarConfiguration={Constants.customHtmlEditorOptions}
									aria-label={'Content'}
								/>
							</div>
							{!readOnly && (
								<ReplacementTags
									handler={this.addPlaceholder}
									showAccessCode={audience.distributionType === DistributionType.SingleLinkWithAccessCode}
								/>
							)}
						</div>
					</Col3Col9>
					{DistributionHelper.isSingleLinkWithDistribution(audience.distributionType) && (
						<SurveyLink toast={this.props.toast} surveyLinkId={audience.surveyLinkId} isMultiLanguageSurvey={isMultiLanguageSurvey} />
					)}
					{!readOnly && (
						<Col3Col9 label='Test Email'>
							<SendTestEmailButton
								id='sendTestEmailButton'
								size='sm'
								error={(!stateValidationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.InviteForm)).toString()}
								onClick={this.openSendEmailModal}
								disabled={this.props.shouldDisableTestEmail}
							>
								{ContentConstants.BUTTONS_SEND_TEST_EMAIL}
							</SendTestEmailButton>
							{!stateValidationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.InviteForm) && (
								<>
									<ErrorMessageLabel className='tws-invite-email-error-message'>
										{stateValidationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.InviteForm)}
									</ErrorMessageLabel>
								</>
							)}

							{sendEmailModalOpen && (
								<SendEmailModal
									modalOpen={sendEmailModalOpen}
									emailTemplate={invitationEmail}
									surveyId={this.props.surveyId}
									shouldReplaceSurveyLink={this.props.isAdminMenu ?? false}
									closeModal={this.closeModal}
								/>
							)}
						</Col3Col9>
					)}
				</InviteEmailContainer>
				<WarningModal
					isOpen={this.props.isTemplateWarningModalOpen}
					contentHeadline={ContentConstants.WARNING_MODAL_ACTION_CANNOT_BE_UNDONE_HEADLINE}
					contentBody={ContentConstants.WARNING_INVITE_EMAIL_TEMPLATE_CHANGE.replace('{template name}', selectedTemplate?.displayText)}
					onSubmit={this.props.submitTemplateWarningModal}
					onClose={() => this.props.CloseTemplateWarningModal()}
				/>
			</>
		);
	}

	private onEnableHtmlContentValueChange(value: boolean) {
		const { invitationEmail, onChange } = this.props;

		this.setState({ validationError: undefined }, () => {
			if (onChange && invitationEmail.enableHtmlContent !== value) {
				const content = new HtmlSanitizer().sanitizeText(invitationEmail.content);
				onChange({ ...invitationEmail, enableHtmlContent: value, content });
			}
		});
	}

	private addPlaceholder(placeholder: string) {
		const { invitationEmail, onChange } = this.props;

		this.setState({ validationError: undefined }, () => {
			if (onChange) {
				onChange({ ...invitationEmail, content: invitationEmail.content + `\r\n${placeholder}` });
			}
		});
	}

	private onChange = (key: string, event: any) => {
		const { invitationEmail, onChange } = this.props;
		// SMELL: without cloning this prop, we mutate redux state. How is that possible?
		const updatedInvitationEmail: any = cloneDeep(invitationEmail);
		if (updatedInvitationEmail[key] === event.target.value) {
			return;
		}
		updatedInvitationEmail[key] = event.target.value;
		if (onChange) {
			onChange(updatedInvitationEmail);
		}
	};

	private onEmailContentChange = (value: string) => {
		const { invitationEmail, onChange } = this.props;
		if (onChange && invitationEmail.content !== value) {
			onChange({ ...invitationEmail, content: value });
		}
	};

	private openSendEmailModal = () => {
		const { toast, toggleSendEmailModal, validateInvitationEmail, audience } = this.props;

		let validationErrors: any;
		if (validateInvitationEmail) {
			validationErrors = validateInvitationEmail(audience.distributionType);
		}
		const inviteFormError = validationErrors
			? {
					InviteForm: [ContentConstants.TOAST_SEND_TEST_EMAIL_VALIDATION],
			  }
			: undefined;
		this.setState({ validationError: inviteFormError }, () => {
			if (validationErrors) {
				if (toast) {
					toast(ToastType.Warning, '');
				}
			} else {
				toggleSendEmailModal(true);
			}
		});
	};

	private closeModal() {
		this.props.toggleSendEmailModal(false);
	}
}
